/**
 * Font faces for outline-rendered text (Typography Pro, curved text).
 *
 * These renderers trace glyph outlines from real font files instead of letting the browser
 * draw them, so the canvas, cart thumbnail and print file all share one geometry. The cost is
 * that a face the font does not ship cannot be synthesized: many display fonts (Knewave, Eater…)
 * only exist at 400, and Google answers `wght@700` with HTTP 400. Requests therefore resolve
 * through a fallback chain to the nearest face that exists, and `faceSupport` tells the host
 * which styles a family really has so it can disable the toolbar buttons it cannot honour.
 *
 * fontkit is injected through `configureCurveFonts` (it lives in each addon's node_modules,
 * not the core's), along with any addon-bundled faces.
 */
import { ref } from 'vue';
import type {} from '@core/veyopRuntime';

declare global {
  interface Window {
    VeyopDesignTemplates?: { pluginBaseUrl?: string };
  }
}

type FontkitPosition = { xAdvance?: number; yAdvance?: number; xOffset?: number; yOffset?: number };
export type FontkitBBox = { minX: number; minY: number; maxX: number; maxY: number };
export type FontkitPath = {
  toSVG?: () => string;
  bbox: FontkitBBox;
  transform: (m0: number, m1: number, m2: number, m3: number, m4: number, m5: number) => FontkitPath;
};
export type FontkitGlyph = { id?: number; path?: FontkitPath };
export type FontkitFont = {
  unitsPerEm?: number;
  ascent?: number;
  descent?: number;
  italicAngle?: number;
  'OS/2'?: { usWeightClass?: number; fsSelection?: { italic?: boolean; bold?: boolean } };
  hasGlyphForCodePoint?: (codePoint: number) => boolean;
  glyphForCodePoint?: (codePoint: number) => { id?: number };
  layout: (text: string) => { glyphs: FontkitGlyph[]; positions: FontkitPosition[] };
};

export type FaceStyle = 'normal' | 'italic';

/** One face request: family + style plus the text the face must cover. */
export type FaceRequest = {
  family: string;
  weight?: unknown;
  style?: unknown;
  /** Text the face must have glyphs for (Google subsets are cut to it). */
  text: string;
  /** A font file pinned on the item; it is the only face the item can use. */
  explicitUrl?: string;
};

export type CatalogFont = { title: string; type: string; filename?: string; path?: string; url?: string; font_preview_url?: string };

/** Resolves an addon-bundled face to a URL, or '' when the addon does not ship that face. */
export type BundledFaceResolver = (family: string, weight: string, style: FaceStyle) => Promise<string>;

export type FaceSupport = { bold: boolean; italic: boolean };

let createFont: ((data: Uint8Array) => unknown) | null = null;
let bundledResolver: BundledFaceResolver | null = null;

/** Wire in fontkit's `create` and the addon's bundled faces. Safe to call more than once. */
export function configureCurveFonts(options: { create: (data: Uint8Array) => unknown; bundled?: BundledFaceResolver }): void {
  createFont = options.create;
  if (options.bundled) bundledResolver = options.bundled;
}

const fontCache = new Map<string, FontkitFont[]>();
const faceInfo = new WeakMap<FontkitFont, { weight: number; italic: boolean }>();
const pending = new Map<string, Promise<FontkitFont | null>>();
const supportCache = new Map<string, FaceSupport>();
const supportPending = new Map<string, Promise<FaceSupport>>();
/** Bumped whenever a face or a support probe lands, so sync readers re-run reactively. */
export const fontCacheRevision = ref(0);
let catalogPromise: Promise<CatalogFont[]> | null = null;

export function cleanFamily(family: unknown): string {
  return String(family ?? 'Arial').replace(/^['"]|['"].*$/g, '').trim() || 'Arial';
}

export function normalizeWeight(weight: unknown): string {
  const value = String(weight ?? '400').trim().toLowerCase();
  if (value === 'normal' || value === 'regular' || value === '') return '400';
  if (value === 'bold' || value === 'bolder') return '700';
  if (value === 'lighter') return '300';
  const numeric = Number(value);
  return Number.isFinite(numeric) ? String(Math.max(100, Math.min(900, Math.round(numeric / 100) * 100))) : '400';
}

export function normalizeStyle(style: unknown): FaceStyle {
  const value = String(style ?? 'normal').trim().toLowerCase();
  return value === 'italic' || value === 'oblique' ? 'italic' : 'normal';
}

export function faceKey(family: unknown, weight: unknown, style: unknown): string {
  return `${cleanFamily(family).toLowerCase()}|${normalizeWeight(weight)}|${normalizeStyle(style)}`;
}

function coverageCharacters(text: string): string[] {
  return [...new Set(Array.from(text.normalize('NFC')))].filter((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    return !/\s/u.test(character) && codePoint !== 0x200d && !(codePoint >= 0xfe00 && codePoint <= 0xfe0f);
  });
}

export function coverageKey(request: FaceRequest): string {
  const codePoints = coverageCharacters(request.text).map((character) => character.codePointAt(0)?.toString(16) ?? '0').sort();
  return `${faceKey(request.family, request.weight, request.style)}|${codePoints.join('.')}`;
}

/** Decoding is lazy: read every glyph outline for the sample text so a broken font is rejected up front. */
function canRenderOutlines(font: FontkitFont, text: string): boolean {
  try {
    const run = font.layout(text.normalize('NFC') || 'Aa');
    for (const glyph of run.glyphs) void glyph.path?.bbox;
    return true;
  } catch {
    return false;
  }
}

export function fontSupportsText(font: FontkitFont, text: string): boolean {
  const characters = coverageCharacters(text);
  if (!characters.length) return true;
  if (font.hasGlyphForCodePoint) return characters.every((character) => font.hasGlyphForCodePoint!(character.codePointAt(0)!));
  if (font.glyphForCodePoint) return characters.every((character) => Number(font.glyphForCodePoint!(character.codePointAt(0)!).id ?? 0) !== 0);
  return false;
}

/** The weight and slant a parsed face really has, read from its own tables. */
export function fontFaceInfo(font: FontkitFont): { weight: number; italic: boolean } {
  const known = faceInfo.get(font);
  if (known) return known;
  const os2 = font['OS/2'];
  const weight = Number(os2?.usWeightClass);
  const italic = Boolean(os2?.fsSelection?.italic) || Math.abs(Number(font.italicAngle) || 0) > 0.5;
  return { weight: Number.isFinite(weight) && weight > 0 ? weight : (os2?.fsSelection?.bold ? 700 : 400), italic };
}

function trimSlash(value: string): string {
  return value.replace(/\/+$/, '');
}

function derivePluginBaseUrlFromScript(): string {
  const script = Array.from(document.scripts)
    .map((item) => item.src)
    .find((src) => /\/plugins\/(typography-pro|text-arc)\/dist\//.test(src));
  const match = script ? /\/plugins\/(typography-pro|text-arc)\//.exec(script) : null;
  if (script && match) return trimSlash(script.slice(0, match.index));
  return '';
}

function baseUrl(): string {
  const designerBase = window.Veyop?.designer?.getContext?.().baseUrl;
  const adminBase = document.querySelector('veyop-admin')?.getAttribute('base-url');
  const templatesBase = window.VeyopDesignTemplates?.pluginBaseUrl;
  const hostBase = window.VeyopHostConfig?.baseUrl || window.Veyop?.runtime?.baseUrl;
  const derived = derivePluginBaseUrlFromScript();
  return trimSlash(String(designerBase || adminBase || templatesBase || hostBase || derived || ''));
}

export async function loadFontCatalog(): Promise<CatalogFont[]> {
  if (catalogPromise) return catalogPromise;
  const root = baseUrl();
  if (!root) {
    catalogPromise = Promise.resolve([]);
    return catalogPromise;
  }
  catalogPromise = fetch(`${root}/ajax.php?type=fonts`, { method: 'POST', credentials: 'same-origin' })
    .then((response) => response.json())
    .then((payload) => Array.isArray(payload?.fonts?.fonts) ? payload.fonts.fonts.map((row: Record<string, unknown>) => ({
      title: String(row.title ?? ''), type: String(row.type ?? ''), filename: row.filename ? String(row.filename) : undefined,
      path: row.path ? String(row.path) : undefined, url: row.url ? String(row.url) : undefined,
      font_preview_url: row.font_preview_url ? String(row.font_preview_url) : undefined,
    })) : [])
    .catch(() => []);
  return catalogPromise;
}

function customFontUrl(font: CatalogFont): string {
  if (!font.filename) return '';
  try {
    const files = JSON.parse(font.filename) as Record<string, string>;
    const file = files.woff2 || files.woff || files.ttf || files.otf || '';
    if (!file) return '';
    if (font.font_preview_url) return font.font_preview_url;
    const prefix = font.url || `${window.location.origin}/${String(font.path ?? '').replace(/^\/+/, '').replace(/\/$/, '')}/`;
    return new URL(file, prefix).href;
  } catch {
    return '';
  }
}

/** An uploaded catalog font ships a single file: it is the family's only face. */
async function catalogFaceUrl(family: string): Promise<string> {
  const catalog = await loadFontCatalog();
  const entry = catalog.find((font) => font.title.trim().toLowerCase() === family.toLowerCase());
  return entry ? customFontUrl(entry) : '';
}

function googleAxis(weight: string, style: FaceStyle): string {
  // `ital,wght@0,400` is accepted by upright-only families too, but `ital,wght@1,…` is the
  // only way to get a true italic: a bare `wght@` request always returns the upright face.
  return style === 'italic' ? `ital,wght@1,${weight}` : `wght@${weight}`;
}

function googleCssUrl(family: string, weight: string, style: FaceStyle, text: string): string {
  return `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:${googleAxis(weight, style)}&display=swap&text=${encodeURIComponent(text || 'Aa')}`;
}

async function googleFontUrl(family: string, weight: string, style: FaceStyle, text: string): Promise<string> {
  const response = await fetch(googleCssUrl(family, weight, style, text));
  // Google answers a face the family does not have with HTTP 400.
  if (!response.ok) return '';
  const css = await response.text();
  const match = css.match(/src:\s*url\(([^)]+)\)\s*format\(['"]woff2['"]\)/i) || css.match(/url\(([^)]+)\)/i);
  return match?.[1]?.replace(/["']/g, '') ?? '';
}

async function googleFaceExists(family: string, weight: string, style: FaceStyle): Promise<boolean> {
  try {
    return (await fetch(googleCssUrl(family, weight, style, 'Aa'))).ok;
  } catch {
    return false;
  }
}

async function bundledFaceUrl(family: string, weight: string, style: FaceStyle): Promise<string> {
  if (!bundledResolver) return '';
  return bundledResolver(family, weight, style).catch(() => '');
}

/**
 * Faces to try for a request, best first: the exact face, then the same weight upright, then
 * regular in the requested slant, then plain regular. Browsers would synthesize the missing
 * style; outline renderers draw the closest real face instead and the host disables the button.
 */
export function faceCandidates(weight: unknown, style: unknown): Array<{ weight: string; style: FaceStyle }> {
  const w = normalizeWeight(weight);
  const s = normalizeStyle(style);
  const list: Array<{ weight: string; style: FaceStyle }> = [
    { weight: w, style: s },
    { weight: w, style: 'normal' },
    { weight: '400', style: s },
    { weight: '400', style: 'normal' },
  ];
  return list.filter((entry, index) => list.findIndex((other) => other.weight === entry.weight && other.style === entry.style) === index);
}

function remember(key: string, font: FontkitFont): void {
  const fonts = fontCache.get(key) ?? [];
  if (!fonts.includes(font)) fontCache.set(key, [...fonts, font]);
}

/** A parsed face that covers the request's text, or null while it is not loaded yet. */
export function getFace(request: FaceRequest): FontkitFont | null {
  // Make renderer/preflight consumers reactive when a lazy font finishes loading.
  void fontCacheRevision.value;
  const fonts = fontCache.get(faceKey(request.family, request.weight, request.style)) ?? [];
  return fonts.find((font) => fontSupportsText(font, request.text)) ?? null;
}

function parseFont(data: Uint8Array): FontkitFont | null {
  if (!createFont) throw new Error('Curve text fonts are not configured');
  const font = createFont(data) as Partial<FontkitFont> | null;
  return typeof font?.layout === 'function' ? font as FontkitFont : null;
}

/** Prime a parsed font from trusted bytes (used by preloaders and deterministic tests). */
export function primeFace(request: FaceRequest, data: Uint8Array): FontkitFont {
  const font = parseFont(data);
  if (!font || !canRenderOutlines(font, request.text)) throw new Error('Unsupported font data');
  remember(faceKey(request.family, request.weight, request.style), font);
  fontCacheRevision.value++;
  return font;
}

async function loadCandidate(url: string, text: string): Promise<FontkitFont | null> {
  if (!url) return null;
  try {
    const response = await fetch(url, { credentials: new URL(url, window.location.href).origin === window.location.origin ? 'same-origin' : 'omit' });
    if (!response.ok) return null;
    const parsed = parseFont(new Uint8Array(await response.arrayBuffer()));
    return parsed && fontSupportsText(parsed, text) && canRenderOutlines(parsed, text) ? parsed : null;
  } catch {
    return null;
  }
}

async function loadFace(family: string, weight: string, style: FaceStyle, text: string, explicitUrl: string): Promise<FontkitFont | null> {
  if (explicitUrl) return loadCandidate(explicitUrl, text);
  // Bundled faces are self-contained: resolve them before the merchant catalog so built-in
  // fonts never cost a request during hydration.
  // Bundled faces only carry the Latin subset, so text in Vietnamese (or any other script) goes
  // on to Google, where the `text=` subset covers exactly what was typed.
  const bundled = await bundledFaceUrl(family, weight, style);
  const bundledFont = bundled ? await loadCandidate(bundled, text) : null;
  if (bundledFont) return bundledFont;
  const custom = await catalogFaceUrl(family);
  if (custom) return loadCandidate(custom, text);
  const google = await googleFontUrl(GOOGLE_ALIASES[family.toLowerCase()] ?? family, weight, style, text).catch(() => '');
  return loadCandidate(google, text);
}

/** System fonts that are not on Google Fonts, mapped to their metric-compatible twins. */
const GOOGLE_ALIASES: Record<string, string> = {
  arial: 'Arimo',
  helvetica: 'Arimo',
  'times new roman': 'Tinos',
  'courier new': 'Cousine',
};

/** Load the closest existing face for a request; null only when no face of the family loads. */
export async function ensureFace(request: FaceRequest): Promise<FontkitFont | null> {
  const cached = getFace(request);
  if (cached) return cached;
  const key = coverageKey(request);
  const existing = pending.get(key);
  if (existing) return existing;
  const family = cleanFamily(request.family);
  const requestedKey = faceKey(family, request.weight, request.style);
  const explicitUrl = String(request.explicitUrl ?? '').trim();
  const task = (async () => {
    try {
      // A pinned file or an uploaded font is a single face: fallbacks would load the same file.
      const candidates = explicitUrl ? faceCandidates(request.weight, request.style).slice(0, 1) : faceCandidates(request.weight, request.style);
      for (const candidate of candidates) {
        const candidateKey = faceKey(family, candidate.weight, candidate.style);
        const known = (fontCache.get(candidateKey) ?? []).find((font) => fontSupportsText(font, request.text));
        const font = known ?? await loadFace(family, candidate.weight, candidate.style, request.text, explicitUrl);
        if (!font) continue;
        if (!faceInfo.has(font)) {
          const info = fontFaceInfo(font);
          faceInfo.set(font, info);
        }
        remember(candidateKey, font);
        remember(requestedKey, font);
        fontCacheRevision.value++;
        return font;
      }
      return null;
    } catch {
      return null;
    } finally {
      pending.delete(key);
    }
  })();
  pending.set(key, task);
  return task;
}

async function probeSupport(family: string, explicitUrl: string, text: string): Promise<FaceSupport> {
  const singleFace = async (): Promise<FaceSupport> => {
    const font = await ensureFace({ family, weight: '400', style: 'normal', text, explicitUrl });
    const info = font ? fontFaceInfo(font) : { weight: 400, italic: false };
    return { bold: info.weight >= 600, italic: info.italic };
  };
  if (explicitUrl) return singleFace();
  const has = async (weight: string, style: FaceStyle) => Boolean(await bundledFaceUrl(family, weight, style));
  const bundledBold = await has('700', 'normal');
  const bundledItalic = await has('400', 'italic');
  if (bundledBold && bundledItalic) return { bold: true, italic: true };
  if (await catalogFaceUrl(family)) return singleFace();
  const googleFamily = GOOGLE_ALIASES[family.toLowerCase()] ?? family;
  const [bold, italic] = await Promise.all([
    bundledBold || googleFaceExists(googleFamily, '700', 'normal'),
    bundledItalic || googleFaceExists(googleFamily, '400', 'italic'),
  ]);
  return { bold, italic };
}

/**
 * Which styles a family really ships, or null while that is still being probed.
 *
 * Reactive: the first call starts a probe (one tiny CSS request per style for Google fonts,
 * the face's own OS/2 table for uploaded fonts) and later calls re-run when it lands.
 */
export function faceSupport(request: Pick<FaceRequest, 'family' | 'explicitUrl' | 'text'>): FaceSupport | null {
  void fontCacheRevision.value;
  const family = cleanFamily(request.family);
  const explicitUrl = String(request.explicitUrl ?? '').trim();
  const key = `${family.toLowerCase()}|${explicitUrl}`;
  const known = supportCache.get(key);
  if (known) return known;
  if (!supportPending.has(key) && typeof window !== 'undefined') {
    const task = probeSupport(family, explicitUrl, request.text)
      .catch(() => ({ bold: true, italic: true }))
      .then((support) => {
        supportCache.set(key, support);
        supportPending.delete(key);
        fontCacheRevision.value++;
        return support;
      });
    supportPending.set(key, task);
  }
  return null;
}

/** Seed a support answer (tests, or hosts that already know the family's faces). */
export function primeFaceSupport(family: string, support: FaceSupport, explicitUrl = ''): void {
  supportCache.set(`${cleanFamily(family).toLowerCase()}|${explicitUrl.trim()}`, support);
  fontCacheRevision.value++;
}

const previewFontCss = new Set<string>();

/** Load a tiny "Aa" Google Fonts subset so CSS swatch previews can show a preset's display font. */
export function ensurePreviewFontCss(family: string, weight = '400'): void {
  const clean = family.trim();
  if (!clean || typeof document === 'undefined') return;
  const key = `${clean.toLowerCase()}|${normalizeWeight(weight)}`;
  if (previewFontCss.has(key)) return;
  previewFontCss.add(key);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(clean)}:wght@${normalizeWeight(weight)}&text=Aa&display=swap`;
  document.head.appendChild(link);
}

export function clearFaceCache(): void {
  fontCache.clear();
  pending.clear();
  supportCache.clear();
  supportPending.clear();
  fontCacheRevision.value++;
}
